import { useEffect, useRef, useState } from 'react';
import { FormattedDate } from 'react-intl';

import { useSurvicate } from 'apps-common/context/survicate';
import { cancelSubscription } from 'apps-common/graphql/cancelSubscription';
import { getLocalStorageUserId, track } from 'apps-common/utils/analytics';
import { throwError } from 'apps-common/utils/errorHandler';
import { logger } from 'apps-common/utils/logger';
import { t } from 'translations';
import { Dialog } from 'ui/components/Dialog';
import { StyledMembershipLogoIcon } from 'ui/components/Icons';
interface CancellationModalProps {
  nextBillingDate?: string;
  showModal: boolean;
  isTrial: boolean;
  billingPeriod: string;
  setShowModal: (showModal: boolean) => void;
  refetchAccountData: () => void;
}

export const CancellationModal = ({
  nextBillingDate,
  showModal,
  isTrial,
  billingPeriod,
  setShowModal,
  refetchAccountData,
}: CancellationModalProps) => {
  const ref = useRef<HTMLDialogElement>(null);
  const survicate = useSurvicate();
  const [cancelInProgress, setCancelInProgress] = useState(false);
  if (!nextBillingDate) {
    throwError('hubGeneralError', 'nextBillingDate is required to cancel');
  }

  useEffect(() => {
    if (showModal) {
      ref.current?.showModal();
    }
  }, [showModal]);

  const onClose = () => {
    ref.current?.close();
    setShowModal(false);
    track({
      event: 'CTA Clicked',
      payload: { cta: 'keep_my_membership', action: 'cancel_membership' },
    });
  };

  const onConfirm = async () => {
    setCancelInProgress(true);
    const res = await cancelSubscription();
    logger.debug('CancelSubscription response', { res });
    ref.current?.close();
    setCancelInProgress(false);
    setShowModal(false);
    // We have to manually trigger survey with custom event
    survicate.invokeSurvey('triggerCancellationV2Survey', {
      analyticsId: getLocalStorageUserId() ?? '',
      env: import.meta.env.MODE ?? '',
      isTrial,
      billingPeriod,
    });
    track({
      event: 'CTA Clicked',
      payload: { cta: 'cancel_my_membership', action: 'cancel_membership' },
    });
    refetchAccountData();
  };

  const TITLE = t('membership_hub_cancel_dialog_title_v2');
  const FIRST_TEXT = t('membership_hub_cancellation_modal_body_text_1_v2');
  const SECOND_TEXT = t('membership_hub_cancellation_modal_body_text_2_v2', {
    expiracy_date: <FormattedDate value={nextBillingDate} format="default" />,
  });
  const THIRD_TEXT = t('membership_hub_cancellation_modal_body_text_3_v2');

  return (
    <Dialog
      ref={ref}
      onCloseClick={onClose}
      icon={<StyledMembershipLogoIcon />}
      title={TITLE}
      textContents={[FIRST_TEXT, SECOND_TEXT, THIRD_TEXT]}
      primaryButton={{
        text: t('membership_hub_cancel_dialog_cancel_membership'),
        onClick: onConfirm,
        disabled: cancelInProgress,
        testId: 'cancel-membership-button',
      }}
      secondaryButton={{
        text: t('membership_hub_cancel_dialog_keep_my_membership'),
        onClick: onClose,
        testId: 'keep-membership-button',
      }}
    ></Dialog>
  );
};
